import type { AppDirectClient } from '../client.js';
import { authBffDebug, refreshTokenMeta } from '../debug.js';
import { AuthError } from '../errors.js';
import { buildSessionCookies } from '../session/cookies.js';
import type { RefreshCoordinator } from '../session/refresh-coordinator.js';
import type { RefreshResult, ResolvedConfig } from '../types.js';
import { assertConfiguredApplicationAssignment } from './entitlement.js';

async function performRefresh(
  config: ResolvedConfig,
  client: AppDirectClient,
  refreshToken: string,
): Promise<RefreshResult> {
  authBffDebug('refresh', 'executing refresh');

  try {
    const authTokens = await client.refreshAuthToken(refreshToken);
    const newRefreshToken = authTokens.refreshToken ?? refreshToken;

    authBffDebug('refresh', 'auth refresh succeeded', {
      returnedNewRefreshToken: authTokens.refreshToken != null,
    });

    if (config.applicationId == null) {
      authBffDebug('refresh', 'entitlement check skipped', { applicationId: null });
    } else {
      authBffDebug('refresh', 'running entitlement check', {
        applicationId: config.applicationId,
      });
      await assertConfiguredApplicationAssignment(config, client, authTokens.accessToken);
      authBffDebug('refresh', 'entitlement check passed', {
        applicationId: config.applicationId,
      });
    }

    const cookiesToSet = buildSessionCookies(config, authTokens.accessToken, newRefreshToken);

    authBffDebug('refresh', 'refresh succeeded', {
      cookieCount: cookiesToSet.length,
      cookieNames: cookiesToSet.map((cookie) => cookie.name),
      fejwtLength: authTokens.accessToken.length,
    });

    return {
      fejwt: authTokens.accessToken,
      refreshToken: newRefreshToken,
      cookiesToSet,
    };
  } catch (err) {
    if (err instanceof AuthError) {
      authBffDebug('refresh', 'refresh failed', {
        code: err.code,
        statusCode: err.statusCode,
        details: err.details,
      });
    } else {
      authBffDebug('refresh', 'refresh failed with unexpected error', {
        name: err instanceof Error ? err.name : 'unknown',
      });
    }
    throw err;
  }
}

export async function refreshSession(
  config: ResolvedConfig,
  client: AppDirectClient,
  coordinator: RefreshCoordinator,
  input: { refreshToken: string },
): Promise<RefreshResult> {
  authBffDebug('refresh', 'refresh requested', {
    refreshToken: refreshTokenMeta(input.refreshToken),
  });

  return coordinator.refreshOnce(input.refreshToken, () =>
    performRefresh(config, client, input.refreshToken),
  );
}
